/**
 * A request that never got an answer: retry a read once, report only what is real (9 Oct).
 *
 * LIVE, 9 Oct: three "Failed to fetch" rows in the Errors view — `GET
 * /api/auth/me` on `/tickets/{id}` and `GET /api/messages/{id}` — all from
 * people whose next request a second later worked. `me` is re-checked when a
 * tab regains focus, so a laptop waking up (Wi-Fi rejoining, a VPN
 * reconnecting, Chrome's ERR_NETWORK_CHANGED on a stale connection) fails the
 * first request and nothing after it. It was never a sign-out: with an expired
 * or missing session the API answers a plain 401 (no redirect to Microsoft),
 * which is an HTTP answer, not a "Failed to fetch".
 *
 * So, in the browser:
 *
 *   - **Reads are retried once.** A `GET`/`HEAD` that fails without an answer
 *     is sent again after `NETWORK_RETRY_DELAY_MS`. If that works, the person
 *     never knew; it is a console warning, not a feedback row. Writes are never
 *     repeated: a `POST` that reached the server before the line dropped would
 *     file twice.
 *   - **Only real failures become feedback.** One that still fails is logged
 *     as before — unless the browser says it is offline, or the page is being
 *     left (signing in again, a reload, closing the tab), when cancelled
 *     requests fail by design. Those are console warnings.
 *
 * 12 Oct: the same row kept coming back (four in a week, `GET /api/auth/me`,
 * last 11:53 UTC) although no LIVE deploy or restart happened after 9 Oct
 * 15:49 UTC and the API answered every probe in about 0.6 s. Still the tab
 * coming back: TanStack re-checks `me` on `visibilitychange`, and a laptop
 * waking up or a VPN reconnecting can take longer than 0.7 s — often with
 * `navigator.onLine` already true. So now:
 *
 *   - **Reads back off:** retried after 1 s, 3 s and 8 s
 *     (`NETWORK_RETRY_DELAYS_MS`). Before each retry the browser waits for
 *     the `online` event if it knows it is offline, and for the page to be
 *     visible again (both capped), via `waitUntilReady`.
 *   - **A background session re-check is never an error.** `me` re-read while
 *     a session is already known only warns, unless the person has been on
 *     the page, online, for `SETTLED_AFTER_VISIBLE_MS` and it still fails —
 *     then it is a real outage, not a wake-up.
 *   - **Every report says what the browser saw** (`describeNetworkFailure`):
 *     online, visible, time since the page became visible, tries and time
 *     spent, foreground or background.
 *
 * Import-free and erasable-syntax TypeScript, so `tests/network-retry.test.mjs`
 * loads it straight from source.
 */

/** Waits before each retry of a read: 1 s, 3 s, 8 s — a wake-up or a VPN reconnect. */
export const NETWORK_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 8000];

/** Longest wait for the `online` event, or for the page to be visible, before a retry. */
export const READY_WAIT_MAX_MS = 30_000;

/**
 * A background `me` re-check that still fails is reported only when the page
 * has been visible this long: past a wake-up or a reconnect, a person on it.
 */
export const SETTLED_AFTER_VISIBLE_MS = 60_000;

/** Methods safe to send twice. */
const RETRYABLE_METHODS = new Set(["GET", "HEAD"]);

/** True for a request the browser cancelled on purpose (navigation, an abort signal). */
export function isAbort(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

/** True when the request's own signal has been aborted. */
function isAborted(request: Request): boolean {
  return request.signal?.aborted === true;
}

/** True when the method may be repeated without side effects. */
export function isRetryableMethod(method: string | undefined): boolean {
  return RETRYABLE_METHODS.has((method ?? "GET").toUpperCase());
}

/** How hard a request that got no answer was tried. */
export interface RetryInfo {
  /** Requests sent, the first one included. */
  attempts: number;
  /** From the first send to the last failure. */
  elapsedMs: number;
}

const retryInfoByError = new WeakMap<object, RetryInfo>();

/** The tries behind a failure thrown by `withNetworkRetry`, if it recorded them. */
export function retryInfoOf(error: unknown): RetryInfo | undefined {
  return typeof error === "object" && error !== null ? retryInfoByError.get(error) : undefined;
}

export interface NetworkRetryOptions {
  /** Waits `ms`; replaced in tests. */
  sleep?: (ms: number) => Promise<void>;
  /** Resolves when a retry is worth sending (online, page visible); never rejects. */
  waitUntilReady?: () => Promise<void>;
  /** Milliseconds now; replaced in tests. */
  now?: () => number;
  /** Told when a retry answered, with the first failure. */
  onRecovered?: (request: Request, firstError: unknown, info: RetryInfo) => void;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Wrap `fetch` so a read with no answer is tried again, backing off.
 *
 * Any HTTP answer (a 401, a 503) is returned as it is: only a request that
 * got none is retried. Writes are sent once. The failure finally thrown
 * carries its `RetryInfo` (see `retryInfoOf`).
 */
export function withNetworkRetry(
  fetchFn: (request: Request) => Promise<Response>,
  options: NetworkRetryOptions = {},
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  const sleep = options.sleep ?? defaultSleep;
  const waitUntilReady = options.waitUntilReady ?? (() => Promise.resolve());
  const now = options.now ?? Date.now;
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request =
      input instanceof Request && init === undefined ? input : new Request(input, init);
    const retryable = isRetryableMethod(request.method);
    const delays = retryable ? NETWORK_RETRY_DELAYS_MS : [];
    const started = now();
    let firstError: unknown = undefined;
    for (let attempt = 0; ; attempt += 1) {
      // A read has no body, but a fresh clone keeps the original sendable either way.
      const toSend = attempt < delays.length ? request.clone() : request;
      try {
        const response = await fetchFn(toSend);
        if (attempt > 0) {
          options.onRecovered?.(request, firstError, {
            attempts: attempt + 1,
            elapsedMs: now() - started,
          });
        }
        return response;
      } catch (error) {
        if (attempt === 0) {
          firstError = error;
        }
        const cancelled = isAbort(error) || isAborted(request);
        if (cancelled || attempt >= delays.length) {
          if (!cancelled && typeof error === "object" && error !== null) {
            retryInfoByError.set(error, { attempts: attempt + 1, elapsedMs: now() - started });
          }
          throw error;
        }
        await sleep(delays[attempt] ?? 0);
        await waitUntilReady();
        // Read again: the signal may have been aborted while waiting.
        if (isAborted(request)) {
          throw error;
        }
      }
    }
  };
}

/** What the browser knew when a request got no answer. */
export interface NetworkFailureContext {
  /** `navigator.onLine`; false only when the browser knows it has no network. */
  online: boolean;
  /** The page is being left (a sign-in redirect, a reload, closing the tab). */
  leaving: boolean;
  /** `document.visibilityState === "visible"`; true when unknown. */
  visible?: boolean;
  /** Since the page last became visible (or loaded); null when unknown. */
  sinceVisibleMs?: number | null;
  /** A re-check of `me` while a session is already known — nobody is waiting on it. */
  background?: boolean;
}

/** What to do with a request that got no answer: log it as feedback, or only warn. */
export function networkFailureAction(context: NetworkFailureContext): "report" | "warn" {
  if (!context.online || context.leaving) {
    return "warn";
  }
  if (context.background === true) {
    // "Try again later": the next focus re-checks it. Only a re-check that
    // fails with someone settled on the page, online, is a real outage.
    const settled =
      context.visible !== false &&
      typeof context.sinceVisibleMs === "number" &&
      context.sinceVisibleMs >= SETTLED_AFTER_VISIBLE_MS;
    return settled ? "report" : "warn";
  }
  return "report";
}

/** True for the session re-check nobody waits on: `me` read again with a session known. */
export function isBackgroundSessionCheck(path: string | undefined, hasSession: boolean): boolean {
  return hasSession && path !== undefined && /\/auth\/me\/?$/.test(path);
}

function seconds(ms: number): string {
  return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

/**
 * One line for the feedback row: what the browser saw when it gave up.
 *
 * e.g. `online=yes visible=yes since-visible=4.2s tries=4 over=12.3s check=background`.
 * No `key=value` the server scrubs (token, secret, …), and well under its 300 characters.
 */
export function describeNetworkFailure(
  context: NetworkFailureContext,
  info: RetryInfo | undefined,
): string {
  const parts = [
    `online=${context.online ? "yes" : "no"}`,
    `visible=${context.visible === false ? "no" : context.visible === true ? "yes" : "unknown"}`,
  ];
  if (typeof context.sinceVisibleMs === "number") {
    parts.push(`since-visible=${seconds(context.sinceVisibleMs)}`);
  }
  if (info !== undefined) {
    parts.push(`tries=${info.attempts}`, `over=${seconds(info.elapsedMs)}`);
  }
  parts.push(`check=${context.background === true ? "background" : "foreground"}`);
  return parts.join(" ");
}
